export default function Footer({ settings }) {
  const year = new Date().getFullYear();
  return (
    <footer data-testid="site-footer" className="relative border-t border-white/5 bg-[#0A0A0C]">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <p className="overline mb-6">Let's build something</p>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl leading-[0.95]">
              Have a project<br />in mind?
            </h2>
            {settings?.tagline && (
              <p data-testid="footer-tagline" className="mt-8 font-serif-accent text-[#8A8A93] text-sm md:text-[15px] tracking-wide">
                {settings.tagline}
              </p>
            )}
            {settings?.contact_email && (
              <a
                href={`mailto:${settings.contact_email}`}
                data-testid="footer-email"
                className="inline-block mt-3 font-display text-lg md:text-xl text-white hover:text-[#C9A050] transition-colors border-b border-white/20 hover:border-[#C9A050] pb-1"
              >
                {settings.contact_email}
              </a>
            )}
            {/* Social icons — Instagram + LinkedIn. Shown when either URL
               is set in Settings admin. If a custom icon image is uploaded
               it renders larger with no border box; otherwise the built-in
               outline SVG is used inside a subtle bordered chip. */}
            {(settings?.instagram_url || settings?.linkedin_url) && (
              <div data-testid="footer-socials" className="mt-6 flex items-center gap-5">
                {settings?.instagram_url && (
                  <a
                    href={settings.instagram_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Instagram"
                    data-testid="footer-instagram"
                    className={
                      settings.instagram_icon
                        ? "block text-white/80 hover:text-[#C9A050] transition-colors"
                        : "w-9 h-9 flex items-center justify-center border border-white/15 text-white/70 hover:text-[#C9A050] hover:border-[#C9A050] transition-colors"
                    }
                  >
                    {settings.instagram_icon ? (
                      <img
                        src={settings.instagram_icon}
                        alt="Instagram"
                        data-testid="footer-instagram-img"
                        className="w-9 h-9 md:w-10 md:h-10 object-contain"
                      />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                      </svg>
                    )}
                  </a>
                )}
                {settings?.linkedin_url && (
                  <a
                    href={settings.linkedin_url}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="LinkedIn"
                    data-testid="footer-linkedin"
                    className={
                      settings.linkedin_icon
                        ? "block text-white/80 hover:text-[#C9A050] transition-colors"
                        : "w-9 h-9 flex items-center justify-center border border-white/15 text-white/70 hover:text-[#C9A050] hover:border-[#C9A050] transition-colors"
                    }
                  >
                    {settings.linkedin_icon ? (
                      <img
                        src={settings.linkedin_icon}
                        alt="LinkedIn"
                        data-testid="footer-linkedin-img"
                        className="w-9 h-9 md:w-10 md:h-10 object-contain"
                      />
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
                        <rect x="2" y="9" width="4" height="12" />
                        <circle cx="4" cy="4" r="2" />
                      </svg>
                    )}
                  </a>
                )}
              </div>
            )}
            {(settings?.contact_phone || settings?.contact_address) && (
              <div data-testid="footer-direct" className="mt-6 space-y-2 text-[#A1A1AA] text-sm md:text-[15px] leading-relaxed">
                {settings?.contact_phone && (
                  <p>
                    <a
                      href={`tel:${settings.contact_phone.replace(/\s+/g, "")}`}
                      data-testid="footer-phone"
                      className="hover:text-[#C9A050] transition-colors"
                    >
                      {settings.contact_phone}
                    </a>
                  </p>
                )}
                {settings?.contact_address && (
                  <p
                    data-testid="footer-address"
                    className="font-serif-accent text-[#8A8A93] whitespace-pre-line max-w-xs"
                  >
                    {settings.contact_address}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="md:col-span-3">
            <p className="overline mb-4">Navigate</p>
            <ul className="space-y-2 text-[#A1A1AA]">
              <li><a href="/" data-testid="footer-nav-home" className="hover:text-white">Home</a></li>
              <li><a href="/work" data-testid="footer-nav-work" className="hover:text-white">Work</a></li>
              <li><a href="/case-studies" data-testid="footer-nav-case-study" className="hover:text-white">Case Study</a></li>
              <li><a href="/marketing" data-testid="footer-nav-marketing" className="hover:text-white">Our Approach</a></li>
              <li><a href="/blog" data-testid="footer-nav-blog" className="hover:text-white">Blog</a></li>
              <li><a href="/about" data-testid="footer-nav-about" className="hover:text-white">About</a></li>
              <li><a href="/contact" data-testid="footer-nav-contact" className="hover:text-white">Contact</a></li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="overline mb-4">Elsewhere</p>
            <ul className="space-y-2 text-[#A1A1AA]">
              {settings?.instagram && <li><a href={settings.instagram} target="_blank" rel="noreferrer" className="hover:text-white">Instagram</a></li>}
              {settings?.linkedin && <li><a href={settings.linkedin} target="_blank" rel="noreferrer" className="hover:text-white">LinkedIn</a></li>}
              {settings?.twitter && <li><a href={settings.twitter} target="_blank" rel="noreferrer" className="hover:text-white">Twitter</a></li>}
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p data-testid="footer-copyright" className="overline">© {year} {settings?.agency_name || "Artistry Perspectives"}</p>
          <p data-testid="footer-note" className="text-xs text-[#8A8A93]">{settings?.footer_note || "Crafted with intention."}</p>
        </div>
      </div>
    </footer>
  );
}
